import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import {
  useRefreshShiftsRows,
  useRefreshStats,
} from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import buildAddPlannedBreaksParameters from 'modules/shift_management/lib/buildAddPlannedBreaksParameters'
import buildAddShiftsParameters from 'modules/shift_management/lib/buildAddShiftsParameters'
import buildEmployeeIds from 'modules/shift_management/lib/buildEmployeeIds'
import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { trackAddEvent } from 'modules/shift_management/lib/trackEvents'
import type { Leave, ShiftsRow } from 'modules/shift_management/lib/types'
import ShiftsApi from 'modules/shift_management/store/api/collections/shifts'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import { useBulkCreatePlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useBulkCreatePlannedBreaks'

import type ShiftForm from '../lib/forms/ShiftForm'
import { ShiftsAttributesByDate } from './useDefaultAttributesFromRows'
import { useSelectionState } from './useSelection'

export interface Props {
  form: ShiftForm
  weekDays: string
  defaultAttributes: ShiftsAttributesByDate
  daysByRow: Record<string, string[]>
  from: 'modal' | 'side_panel' | 'quick_actions'
  advancedOptions?: boolean
  onSuccess?: () => void
  plannedBreakForms?: PlannedBreakForm[]
  isBackup?: boolean
  leave?: Leave
  contractVersions: ContractVersions
}

interface BuildParams {
  defaultShiftId?: number
}

interface SubmitProps {
  closePortal?: ClosePortalFn
  defaultShiftId?: number
  createBreaks?: boolean
}

const useAddCustomShifts = ({
  form,
  weekDays,
  defaultAttributes,
  daysByRow,
  from,
  advancedOptions = false,
  onSuccess,
  plannedBreakForms,
  isBackup,
  leave,
  contractVersions,
}: Props) => {
  const { isEnabled: hasSelection } = useSelectionState()
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()
  const { refreshShiftsRows } = useRefreshShiftsRows(contractVersions)
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)

  const { bulkCreate } = useBulkCreatePlannedBreaks()

  const successMessage = i18n.t('shift_management.actions.add_shifts_success')

  const buildParameters = ({ defaultShiftId }: BuildParams) => {
    return buildAddShiftsParameters({
      view,
      hasSelection: hasSelection,
      defaultAttributesByDate: defaultAttributes,
      name: form.get('name').value,
      unparsedStartTime: form.get('start_time').value,
      unparsedEndTime: form.get('end_time').value,
      daysByRow: daysByRow,
      defaultShiftId,
      extraHours: form.get('extra_hours').value,
      notes: form.get('notes').value,
      locationId: form.get('location_id').value,
      workAreaId: form.get('work_area_id').value,
      color: null,
      templateWeekId: null,
      unassignedShiftId: null,
      state: isBackup ? 'backup' : null,
      leaveId: leave?.id,
    })
  }

  const { running: submitting, performAction: onSubmit } = useAsyncAction(
    async ({ closePortal, defaultShiftId, createBreaks = true }: SubmitProps = {}) => {
      const result = await asyncAction(
        () => {
          const shifts = buildParameters({ defaultShiftId })
          trackAddEvent({
            view,
            isDefaultShift: false,
            weekDays,
            shifts,
            from,
            advancedOptions,
            isBackup,
          })
          return ShiftsApi.rpc('bulk_create', { shifts })
        },
        {
          loadingMessage: i18n.t('shift_management.notices.adding_shifts_notification.description'),
          successFlash: false,
        }
      )

      if (isSuccessful(result)) {
        const { value }: { value: ShiftPayload[] } = result
        const shifts = value.map((shift: ShiftPayload) => Shift.buildFromRequest(shift))

        if (plannedBreakForms && createBreaks) {
          const plannedBreaks = buildAddPlannedBreaksParameters(plannedBreakForms, shifts)
          await asyncAction(() => bulkCreate({ plannedBreaks }), { successFlash: false })
        }

        dispatchUI({ type: 'REFRESHING_STATS' })
        dispatchSchedule({ type: 'ADD_SHIFTS', shifts })
        dispatchShiftsRows({ type: 'ADD_SHIFTS', shifts, view })

        const { employeeIds, newShiftsRowsKeys, shiftsReferenceKeys } = buildEmployeeIds(
          view,
          shiftsRows,
          shifts
        )

        if (newShiftsRowsKeys.length !== 0) {
          await refreshShiftsRows([], employeeIds)
        }

        if (closePortal) closePortal()

        if (onSuccess) onSuccess()

        if (newShiftsRowsKeys.length === 0) {
          // Get shiftsRows where there are affected shifts to update stats
          // This is not necessary when we have added new shiftsRows, as
          // refreshShiftsRows has already fetched the stats
          const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
            return shiftsReferenceKeys.includes(row.key)
          })

          await refreshStats(previousShiftsRows)
        }
      }
    },
    {
      successMessage,
      errorFlash: false,
    }
  )

  return {
    submitting,
    onSubmit,
  }
}

export { useAddCustomShifts }
